export const ACTIVE_RELATIONS_PAGE_SIZE = 100

export const STORE_RELATION_DEACTIVATION_ERRORS = {
  MISSING_CURRENT_SALESMAN_DB_REF:
    'No fue posible reasignar las relaciones activas porque el usuario actual no tiene codigo de usuario.',
  MISSING_NEXT_SALESMAN_DB_REF:
    'No fue posible reasignar las relaciones activas porque el usuario destino no tiene codigo de usuario.',
  MISSING_RELATION_CONFIGURATION:
    'No fue posible procesar una relacion activa porque falta la configuracion de tienda asociada.',
  MISSING_RELATION_CLIENT:
    'No fue posible procesar una relacion activa porque falta el cliente asociado.',
  MISSING_RELATION_SUPPLIER:
    'No fue posible procesar una relacion activa porque falta el proveedor asociado.',
  MISSING_RELATION_BUYER:
    'No fue posible procesar una relacion activa porque falta la cadena asociada.',
  MISSING_RELATION_STORE_REFERENCE:
    'No fue posible procesar una relacion activa porque falta la referencia de tienda.',
  MISSING_BRAND_ASSIGNMENT_SALESMAN:
    'No fue posible reasignar una relacion activa porque una asignacion por marca no tiene usuario asociado.',
  MISSING_BRAND_ASSIGNMENT_BRAND:
    'No fue posible reasignar una relacion activa porque una asignacion por marca no tiene marca asociada.',
  MISSING_STORE_CONFIGURATION:
    'No fue posible reasignar una relacion activa porque falta la configuracion de tienda.',
  MISSING_CLIENT:
    'No fue posible reasignar una relacion activa porque falta el cliente asociado.',
  MISSING_PRINCIPAL_RELATION:
    'No fue posible reasignar una relacion activa porque no tiene usuario principal asociado. Resuelvela desde Relaciones de tienda antes de desactivar el usuario.',
  MISSING_NEXT_PRINCIPAL:
    'No fue posible definir el nuevo usuario principal para una de las relaciones activas.',
  MISSING_EDITABLE_PRINCIPAL:
    'No fue posible reasignar todas las relaciones activas porque una de ellas no tiene un usuario principal editable. Resuelvela desde Relaciones de tienda antes de desactivar el usuario.',
  MISSING_EDITABLE_RELATION:
    'No fue posible reasignar las relaciones activas porque no se encontro una relacion principal editable.',
  NO_REASSIGNMENT_CHANGES:
    'No se encontraron cambios para reasignar en las relaciones activas del usuario.'
}

const normalizeRequiredValue = (value) => {
  return typeof value === 'string' ? value.trim() : value
}

const hasRequiredValue = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  return value !== null && value !== undefined
}

const requireValue = (value, errorMessage) => {
  const normalizedValue = normalizeRequiredValue(value)

  if (!hasRequiredValue(normalizedValue)) {
    throw new Error(errorMessage)
  }

  return normalizedValue
}

const getFirstRequiredValue = (values, errorMessage) => {
  for (const value of values) {
    const normalizedValue = normalizeRequiredValue(value)

    if (hasRequiredValue(normalizedValue)) {
      return normalizedValue
    }
  }

  throw new Error(errorMessage)
}

export const requireSalesmanDbRef = (salesmanDbRef, errorMessage) => {
  return requireValue(salesmanDbRef, errorMessage)
}

const buildRelationDedupKey = (relation) => {
  const configuration = relation?.client_configuration
  const client = relation?.client

  return [
    requireValue(configuration?.id, STORE_RELATION_DEACTIVATION_ERRORS.MISSING_RELATION_CONFIGURATION),
    requireValue(client?.id, STORE_RELATION_DEACTIVATION_ERRORS.MISSING_RELATION_CLIENT),
    requireValue(configuration?.supplier_id, STORE_RELATION_DEACTIVATION_ERRORS.MISSING_RELATION_SUPPLIER),
    getFirstRequiredValue(
      [configuration?.buyer_external_id, configuration?.buyer_id],
      STORE_RELATION_DEACTIVATION_ERRORS.MISSING_RELATION_BUYER
    ),
    requireValue(configuration?.store_reference, STORE_RELATION_DEACTIVATION_ERRORS.MISSING_RELATION_STORE_REFERENCE)
  ].join('|')
}

export const dedupeRelations = (relations) => {
  const seenKeys = new Set()
  const normalizedRelations = Array.isArray(relations) ? relations : []

  return normalizedRelations.filter((relation) => {
    const relationKey = buildRelationDedupKey(relation)

    if (seenKeys.has(relationKey)) return false

    seenKeys.add(relationKey)
    return true
  })
}

const normalizeBrandAssignments = (clientBrands) => {
  const groupedAssignments = new Map()

  ;(Array.isArray(clientBrands) ? clientBrands : []).forEach((salesmanGroup) => {
    const brandRelations = (Array.isArray(salesmanGroup?.brands) ? salesmanGroup.brands : []).filter(Boolean)

    if (!brandRelations.length) return

    const salesmanDbRef = requireValue(
      salesmanGroup?.salesman?.db_ref,
      STORE_RELATION_DEACTIVATION_ERRORS.MISSING_BRAND_ASSIGNMENT_SALESMAN
    )
    const salesmanKey = getFirstRequiredValue(
      [salesmanGroup?.salesman?.id, salesmanDbRef],
      STORE_RELATION_DEACTIVATION_ERRORS.MISSING_BRAND_ASSIGNMENT_SALESMAN
    )
    const currentAssignment = groupedAssignments.get(salesmanKey) || {
      salesmanDbRef,
      brandDbRefs: []
    }
    const brandDbRefs = brandRelations.map((brandRelation) => {
      return requireValue(
        brandRelation?.brand?.db_ref,
        STORE_RELATION_DEACTIVATION_ERRORS.MISSING_BRAND_ASSIGNMENT_BRAND
      )
    })

    currentAssignment.brandDbRefs = Array.from(
      new Set([...currentAssignment.brandDbRefs, ...brandDbRefs])
    )

    groupedAssignments.set(salesmanKey, currentAssignment)
  })

  return Array.from(groupedAssignments.values()).filter((assignment) => assignment.brandDbRefs.length > 0)
}

const buildReassignedBrandRelations = ({ clientBrands, currentSalesmanDbRef, nextSalesmanDbRef }) => {
  const normalizedCurrentSalesmanDbRef = requireSalesmanDbRef(
    currentSalesmanDbRef,
    STORE_RELATION_DEACTIVATION_ERRORS.MISSING_CURRENT_SALESMAN_DB_REF
  )
  const normalizedNextSalesmanDbRef = requireSalesmanDbRef(
    nextSalesmanDbRef,
    STORE_RELATION_DEACTIVATION_ERRORS.MISSING_NEXT_SALESMAN_DB_REF
  )
  const normalizedAssignments = normalizeBrandAssignments(clientBrands)
  const mergedAssignments = new Map()

  normalizedAssignments.forEach((assignment) => {
    const targetSalesmanDbRef = assignment.salesmanDbRef === normalizedCurrentSalesmanDbRef
      ? normalizedNextSalesmanDbRef
      : assignment.salesmanDbRef
    const currentMergedAssignment = mergedAssignments.get(targetSalesmanDbRef) || {
      salesman_db_ref: targetSalesmanDbRef,
      brand_db_refs: []
    }

    currentMergedAssignment.brand_db_refs = Array.from(
      new Set([
        ...currentMergedAssignment.brand_db_refs,
        ...assignment.brandDbRefs
      ])
    )

    mergedAssignments.set(targetSalesmanDbRef, currentMergedAssignment)
  })

  return Array.from(mergedAssignments.values()).filter((assignment) => assignment.brand_db_refs.length > 0)
}

const relationIncludesSalesman = (storeRelation, salesmanDbRef) => {
  if (!salesmanDbRef) return false

  const principalSalesmanDbRef = storeRelation?.client_principal?.salesman?.db_ref?.trim() ?? null

  if (principalSalesmanDbRef === salesmanDbRef) {
    return true
  }

  return (Array.isArray(storeRelation?.client_brands) ? storeRelation.client_brands : []).some((salesmanGroup) => {
    return salesmanGroup?.salesman?.db_ref?.trim() === salesmanDbRef
  })
}

export const buildUpdatePayloadForReassignment = ({
  storeRelation,
  currentSalesmanDbRef,
  nextSalesman
}) => {
  const normalizedCurrentSalesmanDbRef = requireSalesmanDbRef(
    currentSalesmanDbRef,
    STORE_RELATION_DEACTIVATION_ERRORS.MISSING_CURRENT_SALESMAN_DB_REF
  )
  const nextSalesmanDbRef = requireSalesmanDbRef(
    nextSalesman?.db_ref,
    STORE_RELATION_DEACTIVATION_ERRORS.MISSING_NEXT_SALESMAN_DB_REF
  )
  const configuration = storeRelation?.client_configuration
  const client = storeRelation?.client
  const principalRelationId = storeRelation?.client_principal?.id
  const principalSalesmanDbRef = storeRelation?.client_principal?.salesman?.db_ref?.trim() ?? null

  if (!configuration?.id || !configuration?.supplier_id || !configuration?.buyer_id || !configuration?.store_reference) {
    throw new Error(STORE_RELATION_DEACTIVATION_ERRORS.MISSING_STORE_CONFIGURATION)
  }

  if (!client?.id || !client?.db_ref) {
    throw new Error(STORE_RELATION_DEACTIVATION_ERRORS.MISSING_CLIENT)
  }

  if (!principalRelationId || !principalSalesmanDbRef) {
    throw new Error(STORE_RELATION_DEACTIVATION_ERRORS.MISSING_PRINCIPAL_RELATION)
  }

  if (!relationIncludesSalesman(storeRelation, normalizedCurrentSalesmanDbRef)) {
    return null
  }

  const nextPrincipalSalesmanDbRef = principalSalesmanDbRef === normalizedCurrentSalesmanDbRef
    ? nextSalesmanDbRef
    : principalSalesmanDbRef
  const clientBrandRelations = buildReassignedBrandRelations({
    clientBrands: storeRelation?.client_brands,
    currentSalesmanDbRef: normalizedCurrentSalesmanDbRef,
    nextSalesmanDbRef
  })
  const shouldSendBrandAssignments = Boolean(
    storeRelation?.flags?.split_order_by_brand || clientBrandRelations.length > 0
  )

  if (!nextPrincipalSalesmanDbRef) {
    throw new Error(STORE_RELATION_DEACTIVATION_ERRORS.MISSING_NEXT_PRINCIPAL)
  }

  const payload = {
    client_configuration_id: configuration.id,
    supplier_id: configuration.supplier_id,
    buyer_id: configuration.buyer_id,
    store_reference: configuration.store_reference,
    client_id: client.id,
    client_db_ref: client.db_ref,
    principal_salesman_db_ref: nextPrincipalSalesmanDbRef
  }

  if (shouldSendBrandAssignments) {
    payload.allow_brand_assignments = clientBrandRelations.length > 0
    payload.client_brand_relations = clientBrandRelations
  }

  return {
    relationId: principalRelationId,
    payload
  }
}
